import {Component, OnInit, inject, signal} from '@angular/core';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {TranslationService} from '../../../core/services/translation.service';
import {HupSubscription} from '../../../data/models/subscription.model';
import {ConfigService} from "../../../core/services/config.service";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {BaseDataService} from "../../../core/services/base-data.service";
import {BaseData} from "../../../data/models/base-data.model";
import {PaymentService} from "../../../data/services/payment.service";
import {FormOfPayment} from "../../../data/models/formOfPayment.model";
import {LoginService} from "../../../core/components/login/login.service";
import {UserData} from "../../../data/models/user-data.model";

import {
  ProductVariantPeriodBlockComponent
} from "./product-variant-period-block/product-variant-period-block.component";
import {AddressDetailComponent} from "../../../core/components/address-detail/address-detail.component";
import {MatButton} from "@angular/material/button";
import {BankAccountPipe} from "../../../core/pipes/bank-account.pipe";
import {LoadingSpinnerComponent} from "../../../core/components/loading-spinner/loading-spinner.component";
import {MatCard, MatCardContent} from "@angular/material/card";
import {ApplyIfNotEmptyDirective} from "../../../core/directives/apply-if-not-empty.directive";
import {MatIcon} from '@angular/material/icon';
import {OnlinePaymentChecker} from "../../../core/components/utils/online-payment-checker";
import {LandingPageService} from "../../../data/services/landing-page.service";
import {SourceSystemName} from "../../../data/models/source-system-name";

const VALID_BLOCK_KEYS = ['PAYMENT', 'CONTRACT_CURRENT', 'CONTRACT_FUTURE', 'ADR_DELIVERY', 'ADR_BILLING'] as const;

export type SubscriptionBlockKey = typeof VALID_BLOCK_KEYS[number];

@Component({
  selector: 'app-hup-subscription-detail',
  templateUrl: './hup-subscription-detail.component.html',
  styleUrls: ['./hup-subscription-detail.component.scss'],
  imports: [
    ProductVariantPeriodBlockComponent,
    AddressDetailComponent,
    MatButton,
    BankAccountPipe,
    LoadingSpinnerComponent,
    MatCard,
    MatCardContent,
    ApplyIfNotEmptyDirective,
    RouterLink,
    MatIcon,
  ],
  standalone: true
})
export class HupSubscriptionDetailComponent implements OnInit {
  private hupSubscriptionsService = inject(HupSubscriptionsService);
  private route = inject(ActivatedRoute);
  translationService = inject(TranslationService);
  private configService = inject(ConfigService);
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private baseData = inject(BaseDataService);
  private paymentService = inject(PaymentService);
  private loginService: LoginService = inject(LoginService);
  private readonly landingPageService = inject(LandingPageService);
  private readonly SOURCESYSTEM_NAME_URL_CONFIG_KEY = 'sourceSystem.NAME.url';

  private onlinePaymentChecker: OnlinePaymentChecker = new OnlinePaymentChecker();

  sub: HupSubscription;
  showAdditionalSubscriptionInfos: boolean = true;
  showTerminationButton: boolean = true;
  currentDate = new Date();
  invoiceMethods: BaseData[] = [];
  paymentMethods: BaseData[] = [];
  showBackToOverviewButton: boolean;
  paymentMethodId: FormOfPayment;
  paymentMethodDesc: string = "";
  aggregateSubscription: boolean = false;
  showSequelSubscriptionInAggregateMode: boolean = false;
  isLoading: boolean = false;
  userData: UserData;
  blockOrder: SubscriptionBlockKey[] = ['PAYMENT', 'CONTRACT_CURRENT', 'CONTRACT_FUTURE', 'ADR_DELIVERY', 'ADR_BILLING'];
  readonly isMoveServiceActive = signal(false);
  private sourceSystemData = {name: undefined, url: undefined};

  constructor() {
    this.isLoading = true;

    this.showAdditionalSubscriptionInfos = this.configService.getConfig('showAdditionalSubscriptionInfos')?.asBoolean();

    this.showTerminationButton = this.configService.getConfig('subscription_show_termination_button')?.asBoolean();

    this.showBackToOverviewButton = this.configService.getConfig('show.backToOverView.button')?.asBoolean();

    this.aggregateSubscription = this.configService.getConfig('subscription.aggregateSequelAndCombi')?.asBoolean();

    this.showSequelSubscriptionInAggregateMode = this.configService.getConfig('subscription.showSequelInAggregateMode')?.asBoolean();

    let confBlockOrder = this.configService.getConfig('subscription.blockOrder');
    if (!!confBlockOrder?.value) {
      this.blockOrder = confBlockOrder.value
        .split(',')
        .map(k => k.trim())
        .filter((key): key is SubscriptionBlockKey => VALID_BLOCK_KEYS.includes(key as SubscriptionBlockKey));
    }

    this.invoiceMethods = this.baseData.getBaseData('invoicemethods');
    this.paymentMethods = this.baseData.getBaseData('paymentmethods');

    this.landingPageService.isMoveServiceActive().subscribe(this.isMoveServiceActive.set);
  }

  ngOnInit(): void {
    const onlinePaymentConfig = this.configService.getConfig(this.onlinePaymentChecker.ONLINE_PAYMENT_METHOD_ID_CONFIG_KEY);
    if (onlinePaymentConfig && onlinePaymentConfig.value) {
      this.onlinePaymentChecker.setPaymentCode(onlinePaymentConfig.value);
    }

    this.route.paramMap.subscribe(params => {
      this.paymentMethodId = null;
      if (this.aggregateSubscription) {
        this.hupSubscriptionsService.getSubscriptionForDetail(params.get('webId'), params.get('backendId')).subscribe(sub => {
          this.sub = sub;
          this.handleSubscription();
          this.handleSourceSystemData(this.sub);
        });
      } else {
        this.hupSubscriptionsService.getSubscription(params.get('webId'), params.get('backendId')).subscribe(sub => {
          this.sub = sub;
          this.handleSubscription();
          this.handleSourceSystemData(this.sub);
        });
      }
    });
    this.loginService.getUserData().subscribe(userData => {
      if (userData) {
        this.userData = userData;
      }
    });
  }

  private handleSubscription() {
    this.paymentService.getPaymentMethodByCode(this.sub?.curPayment?.paymentType?.paymentMethod).subscribe(result => {
      this.paymentMethodId = result;
    });
    const payment = this.paymentMethods?.filter(method => method.key === this.sub?.curPayment?.paymentType?.paymentMethod)[0]?.description;
    const frequency = this.invoiceMethods?.filter(method => method.key === this.sub?.curPayment?.paymentType?.paymentFrequency)[0]?.description;
    this.paymentMethodDesc = payment ?? this.sub.curPayment.paymentType.paymentMethod;
    this.sub!.curPayment!.paymentType!.paymentFrequency = frequency ?? this.sub.curPayment.paymentType.paymentFrequency;
    if (this.sub.validDate.validUntil !== null && this.sub.validDate.validUntil < this.currentDate) {
      this.showTerminationButton = false;
    }
    this.isLoading = false;
  }

  currentAndSequelSubscriptionsAreSimilar(){
    if (!(!!this.sub.combiSubscriptionList && !!this.sub.sequelSubscriptions)) {
      return false;
    }

    if (this.sub.combiSubscriptionList.length !== this.sub.sequelSubscriptions.length) {
      return false;
    }

    return this.sub.combiSubscriptionList.every((currentSub, index) => {
      const sequelSub = this.sub.sequelSubscriptions[index];
      let subsAreSimilar = sequelSub &&
        currentSub.productCode === sequelSub.productCode &&
        currentSub.variantCode === sequelSub.variantCode &&
        currentSub.priceId === sequelSub.priceId;

      if (subsAreSimilar) {
        sequelSub.validDate.validFrom = currentSub.validDate.validFrom;
      }

      return subsAreSimilar;
    })
  }

  terminateSubscription() {
    const isInternal = this.sourceSystemData.name == SourceSystemName[SourceSystemName.WEBABO];

    if (isInternal) {
      this.router.navigate(['/terminate-subscription/' + this.sub.subId], {queryParamsHandling: "preserve"}).then(r => console.debug('Interne Navigation', r));
    } else {
      const externalUrl = this.sourceSystemData.url;
      if (externalUrl && externalUrl !== '' && externalUrl.trim() != '') {
        this.navigationService.navigateExtern(externalUrl, true).then(r => console.debug('Externe Navigation', r));
      }
    }
  }

  get navigationToExternalOrderImpossible() {
    return this.sourceSystemData.name != SourceSystemName[SourceSystemName.WEBABO] &&
      (!this.sourceSystemData.url || this.sourceSystemData.url == '' || this.sourceSystemData.url?.trim() == '');
  }

  backToOverview(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION, null, true);
  }

  /**
   * Kann das Abonnement storniert werden?
   * Voraussetzungen für mögliche Stornierung:
   * 1. Keine Abokündigung liegt zur Überprüfung vor (= Keine Kündigungsanfrage liegt in der WebDB vor).
   * @param subscription - Abo, wessen Kündbarkeit überprüft werden soll.
   */
  isTerminatable(subscription: HupSubscription) {
    return !(subscription.webId > 0 && subscription.backendId > 0 && subscription.state === -1);
  }

  protected isOnlinePayment(sub: HupSubscription) {
    return this.onlinePaymentChecker.isOnlinePayment(sub.curPayment);
  }

  private handleSourceSystemData(subscription: HupSubscription) {
    if (subscription) {
      this.sourceSystemData.name = subscription.sourceSystem?.systemName;
      if (this.sourceSystemData.name != SourceSystemName[SourceSystemName.WEBABO]) {
        const configKey = this.SOURCESYSTEM_NAME_URL_CONFIG_KEY.replace('NAME', this.sourceSystemData.name);
        this.sourceSystemData.url = this.configService.getConfig(configKey).value;
      }
    }
  }
}
